import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

export const LISA_PRIMARY = {
  50: '#edf5ee',
  100: '#d7e9da',
  200: '#b2d3b8',
  300: '#8dbd96',
  400: '#68a674',
  500: '#4a9e57',
  600: '#3a7e44',
  700: '#2d6536',
  800: '#24512c',
  900: '#1f4325',
  950: '#102415',
} as const;

/** Orchid-Pink primary scale — matches --lisa-btn-primary (hsl 320, 35%) */
export const LISA_ORCHID = {
  50: '#faf5fa',
  100: '#f3e9f2',
  200: '#e4cde1',
  300: '#d2aecf',
  400: 'hsl(320, 35%, 55%)',
  500: 'hsl(320, 35%, 47%)',
  600: 'hsl(320, 35%, 40%)',
  700: 'hsl(320, 35%, 33%)',
  800: '#663361',
  900: '#542c51',
  950: '#361b35',
} as const;

export const LISA_SURFACE_LIGHT = {
  0: '#ffffff',
  50: '#f8fafc',
  100: '#f1f5f9',
  200: '#e2e8f0',
  300: '#cbd5e1',
  400: '#94a3b8',
  500: '#64748b',
  600: '#475569',
  700: '#334155',
  800: '#1e293b',
  900: '#0f172a',
  950: '#020617',
} as const;

export const CrimsonNoirPreset = definePreset(Aura, {
  semantic: {
    // Admin shell / login / dialogs — orchid. Onboarding wizards (add farmer, etc.) use scoped green CTAs.
    primary: {
      ...LISA_ORCHID,
    },
    colorScheme: {
      light: {
        surface: {
          ...LISA_SURFACE_LIGHT,
        },
        primary: {
          color: '{primary.600}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.700}',
          // Match idle fill — avoids a darker flash on mouse/touch press.
          activeColor: '{primary.600}',
        },
        // A disabled field is still a field: PrimeNG fills it with the slate
        // `surface.200` (#e2e8f0), which is not in the palette (#141). It keeps
        // the same soft surface as an editable one and is told apart by its
        // muted text instead.
        formField: {
          // Every editable control sits on the same soft surface. PrimeNG
          // defaults this to white and injects its theme after the stylesheet,
          // so a CSS rule alone never wins; it belongs here.
          background: '#fafafa',
          filledBackground: '#fafafa',
          filledHoverBackground: '#fafafa',
          filledFocusBackground: '#fafafa',
          disabledBackground: '#fafafa',
          disabledColor: '{surface.500}',
        },
        highlight: {
          background: 'color-mix(in srgb, #9c4f96, white 88%)',
          focusBackground: 'color-mix(in srgb, #9c4f96, white 82%)',
          color: '#663361',
          focusColor: '#542c51',
        },
      },
      dark: {
        surface: {
          0: '#ffffff',
          50: '#f8fafc',
          100: '#e2e8f0',
          200: '#cbd5e1',
          300: '#94a3b8',
          400: '#64748b',
          500: '#475569',
          600: '#334155',
          700: '#1e293b',
          800: '#111827',
          900: '#0f172a',
          950: '#020617',
        },
        primary: {
          color: '{primary.400}',
          contrastColor: '{surface.950}',
          hoverColor: '{primary.300}',
          activeColor: '{primary.400}',
        },
        highlight: {
          background: 'color-mix(in srgb, #9c4f96, transparent 84%)',
          focusBackground: 'color-mix(in srgb, #9c4f96, transparent 76%)',
          color: '#f8fafc',
          focusColor: '#ffffff',
        },
      },
    },
  },
  components: {
    // The calendar button is part of the field, not a separate control. PrimeNG
    // fills it with its own grey surface, which is not in the palette (#141),
    // so it takes the field's own background and border here.
    datepicker: {
      dropdown: {
        background: 'transparent',
        hoverBackground: 'transparent',
        activeBackground: 'transparent',
        borderColor: 'transparent',
        hoverBorderColor: 'transparent',
        activeBorderColor: 'transparent',
        color: '{surface.500}',
        hoverColor: '{surface.700}',
        activeColor: '{surface.700}',
      },
    },
    button: {
      colorScheme: {
        light: {
          root: {
            primary: {
              // active = hover so pressing keeps the hover look
              activeBackground: '{primary.hover.color}',
              activeBorderColor: '{primary.hover.color}',
            },
            secondary: {
              activeBackground: '{surface.200}',
              activeBorderColor: '{surface.200}',
              activeColor: '{surface.700}',
            },
            danger: {
              activeBackground: '{red.600}',
              activeBorderColor: '{red.600}',
            },
          },
          outlined: {
            primary: {
              activeBackground: '{primary.50}',
            },
            secondary: {
              activeBackground: '{surface.100}',
            },
            plain: {
              activeBackground: '{surface.100}',
            },
          },
          text: {
            primary: {
              activeBackground: '{primary.50}',
            },
            secondary: {
              activeBackground: '{surface.100}',
            },
            plain: {
              activeBackground: '{surface.100}',
            },
          },
        },
        dark: {
          root: {
            primary: {
              activeBackground: '{primary.300}',
              activeBorderColor: '{primary.300}',
            },
            secondary: {
              activeBackground: '{surface.700}',
              activeBorderColor: '{surface.700}',
              activeColor: '{surface.200}',
            },
            danger: {
              activeBackground: '{red.300}',
              activeBorderColor: '{red.300}',
            },
          },
          outlined: {
            primary: {
              activeBackground: 'color-mix(in srgb, {primary.color}, transparent 92%)',
            },
            secondary: {
              activeBackground: 'rgba(255,255,255,0.06)',
            },
            plain: {
              activeBackground: '{surface.700}',
            },
          },
          text: {
            primary: {
              activeBackground: 'color-mix(in srgb, {primary.color}, transparent 92%)',
            },
            secondary: {
              activeBackground: '{surface.700}',
            },
            plain: {
              activeBackground: '{surface.700}',
            },
          },
        },
      },
    },
  },
});
